import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAdvancedSearchTheme } from '../theme/useAdvancedSearchTheme';
import type { SearchField, FieldOption } from '@/shared/ui/search/AdvancedSearch/types/field.types';
import type { FilterValue } from '@/shared/ui/search/AdvancedSearch/types/filter.types';

interface SearchableSelectProps {
  field: SearchField;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  multiple?: boolean;
}

export function SearchableSelect({
  field,
  value,
  onChange,
  multiple = false,
}: SearchableSelectProps) {
  const { valueInputSx } = useAdvancedSearchTheme();
  const config = field.searchableConfig;

  if (!config) {
    console.error('SearchableSelect requires searchableConfig');
    return null;
  }

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FieldOption[]>(() => {
    const initial = config.initialOptions || field.options || [];
    return Array.from(initial);
  });
  const [loading, setLoading] = useState(false);

  const filteredOptions = useMemo(() => {
    return getFilteredOptions(config.searchType, inputValue, options);
  }, [config.searchType, inputValue, options]);

  const performRemoteSearch = useCallback(
    async (searchText: string) => {
      await performRemoteSearchImpl(config, searchText, setOptions);
    },
    [config]
  );

  useEffect(() => {
    if (config.searchType !== 'remote') return;

    const debounceMs = config.debounceMs || 300;
    const timer = setTimeout(() => {
      performRemoteSearch(inputValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [inputValue, config.searchType, performRemoteSearch]);

  const selectedOption = useMemo(() => {
    return getSelectedOption(value, options, multiple);
  }, [value, options, multiple]);

  return (
    <Autocomplete
      multiple={multiple}
      value={selectedOption}
      onChange={(event, newValue) => handleChange(newValue, multiple, onChange)}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={config.searchType === 'local' ? filteredOptions : options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      loading={loading}
      noOptionsText={config.noOptionsText || '옵션이 없습니다'}
      loadingText={config.loadingText || '로딩 중...'}
      size="small"
      sx={valueInputSx}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={field.placeholder || '검색...'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

function getFilteredOptions(
  searchType: string,
  inputValue: string,
  options: FieldOption[]
): FieldOption[] {
  if (searchType === 'local') {
    if (!inputValue) return options;
    const searchLower = inputValue.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(searchLower));
  }
  return options;
}

async function performRemoteSearchImpl(
  config: any,
  searchText: string,
  setOptions: (options: FieldOption[]) => void
) {
  if (!config.loadOptions) return;

  const minLength = config.minSearchLength || 0;
  if (searchText.length < minLength) {
    const initial = config.initialOptions || [];
    setOptions(Array.from(initial));
    return;
  }

  try {
    const results = await config.loadOptions(searchText);
    setOptions(Array.from(results));
  } catch (error) {
    console.error('Search failed:', error);
    setOptions([]);
  }
}

function getSelectedOption(
  value: FilterValue,
  options: FieldOption[],
  multiple: boolean
): FieldOption | FieldOption[] | null {
  if (multiple) {
    if (!Array.isArray(value)) return [];
    return options.filter((opt) => value.includes(opt.value));
  } else {
    if (value === null || value === undefined) return null;
    return options.find((opt) => opt.value === value) || null;
  }
}

function handleChange(
  newValue: FieldOption | FieldOption[] | null,
  multiple: boolean,
  onChange: (value: FilterValue) => void
) {
  if (multiple) {
    const values = (newValue as FieldOption[]).map((opt) => opt.value);
    onChange(values);
  } else {
    onChange((newValue as FieldOption | null)?.value || null);
  }
}
